import React from "react";
import { Link } from "react-router-dom";

const HowItWorks = () => {
  const steps = [
    {
      number: "01",
      title: "Create Your Form Instantly",
      desc: "Use our intuitive builder to design your form in minutes. Choose text fields, dropdowns, and checkboxes — no coding required.",
      icon: "edit_note",
    },
    {
      number: "02",
      title: "Secure Endpoint Generated",
      desc: "ContactHQ creates a secure backend endpoint automatically. All data is encrypted and protected without needing your own server.",
      icon: "security",
    },
    {
      number: "03",
      title: "Connect & Embed Anywhere",
      desc: "Copy a simple snippet or CDN link to embed your form on WordPress, Shopify, Netlify, or static HTML pages. It's instantly live.",
      icon: "code_blocks",
    },
    {
      number: "04",
      title: "Real-Time Notifications",
      desc: "Submissions flow into your centralized inbox instantly. View, assign, tag, and reply to leads all from one dashboard.",
      icon: "notifications_active",
    },
    {
      number: "05",
      title: "Track, Manage, & Optimize",
      desc: "Monitor responses and team activity. ContactHQ acts as your collaboration hub, making lead management fast and reliable.",
      icon: "analytics",
    },
  ];

  return (
    <section id="how-it-works" className="py-16 sm:py-20 lg:py-24 bg-white overflow-hidden">
      <div className="mx-auto w-[95%] md:w-[90%]">
        {/* SECTION HEADER */}
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-20">
          <h2 className="text-sm sm:text-base font-bold tracking-[0.2em] text-[#E63946] uppercase mb-3 sm:mb-4">
            The Process
          </h2>
          <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0B1C2D] lg:text-5xl mb-4 sm:mb-6">
            Simple, Secure, Ready in Minutes.
          </h3>
          <p className="text-base sm:text-lg text-[#64748B]">
            No technical setup. No backend headaches. Just simplicity that
            works. Getting your forms online shouldn’t feel like launching a
            rocket.
          </p>
        </div>

        {/* STEPS GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12 relative">
          {steps.map((step, index) => (
            <div
              key={index}
              className="group relative p-6 sm:p-8 rounded-3xl bg-slate-50 border border-[#E2E8F0] hover:border-[#E63946]/30 hover:bg-white hover:shadow-2xl hover:shadow-slate-200 transition-all duration-500 opacity-0 animate-fade-in-up"
              style={{ animationDelay: `${index * 150}ms` }}
            >
              {/* Step Number Badge */}
              <div className="absolute -top-4 -left-4 size-10 rounded-xl bg-[#0B1C2D] text-white flex items-center justify-center font-bold text-sm shadow-lg group-hover:bg-[#E63946] transition-colors duration-300">
                {step.number}
              </div>

              {/* Icon */}
              <div className="size-14 rounded-2xl bg-white border border-[#E2E8F0] flex items-center justify-center text-[#0B1C2D] mb-6 group-hover:scale-110 transition-transform duration-300">
                <span className="material-symbols-outlined text-3xl group-hover:text-[#E63946]">
                  {step.icon}
                </span>
              </div>

              {/* Content */}
              <h4 className="text-xl font-bold text-[#0B1C2D] mb-4">
                {step.title}
              </h4>
              <p className="text-[#64748B] text-sm leading-relaxed">
                {step.desc}
              </p>
            </div>
          ))}

          {/* FINAL CTA CARD (Fills the 6th slot in the grid) */}
          <div className="p-8 rounded-3xl bg-[#0B1C2D] flex flex-col justify-center items-center text-center opacity-0 animate-fade-in-up [animation-delay:900ms]">
            <h4 className="text-xl font-bold text-white mb-4">
              Ready to start?
            </h4>
            <p className="text-slate-400 text-sm mb-6">
              Go from zero to live in under five minutes.
            </p>
            <Link to={"/register"} className="w-full py-3 rounded-xl bg-[#E63946] text-white font-bold hover:bg-[#d12f3c] transition-all">
              Create Your First Form
            </Link>
          </div>
        </div>

        {/* WHY YOU'LL LOVE IT BAR */}
        <div className="mt-12 sm:mt-20 border-t border-[#E2E8F0] pt-8 sm:pt-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            {[
              { label: "No Backend Needed", icon: "cloud_off" },
              { label: "Secure & Compliant", icon: "gpp_good" },
              { label: "India & Global Ready", icon: "language" },
              { label: "Lightning-Fast Setup", icon: "bolt" },
            ].map((benefit, i) => (
              <div
                key={i}
                className="flex flex-col items-center gap-2 sm:gap-3 text-center"
              >
                <span className="material-symbols-outlined text-[#E63946]">
                  {benefit.icon}
                </span>
                <span className="text-[10px] sm:text-xs font-bold text-[#0B1C2D] uppercase tracking-wider px-1">
                  {benefit.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
